import { useEffect, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  setPostReaction,
  subscribeToPostReaction,
  type Reaction,
  type PublicPost,
} from '@/src/features/posts/post-service';
import {
  isNetworkAvailable,
  isRecoverableNetworkError,
  queueSetPostReaction,
} from '@/src/features/offline/outbox-service';
import { trackEvent } from '@/src/lib/analytics';
import {
  profileStatsQueryKey,
  publicProfileStatsQueryKey,
} from '@/src/features/profile/use-profile';
import { isPermissionDeniedError } from '@/src/utils/errors';

type PostReactionState = {
  error: Error | null;
  loading: boolean;
  mutationId: string | null;
  reaction: Reaction | null;
};

const REACTION_WRITE_TIMEOUT_MS = 8000;

async function withReactionWriteTimeout<T>(promise: Promise<T>) {
  let timeout: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => {
          reject(new Error('Post reaction update timed out.'));
        }, REACTION_WRITE_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}

export function usePostReaction(
  postId: string,
  uid: string | undefined,
  enabled = true,
) {
  const [state, setState] = useState<PostReactionState>({
    error: null,
    loading: Boolean(uid),
    mutationId: null,
    reaction: null,
  });

  useEffect(() => {
    if (!uid || !enabled) {
      setState({
        error: null,
        loading: false,
        mutationId: null,
        reaction: null,
      });
      return undefined;
    }

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: true,
    }));

    return subscribeToPostReaction({
      postId,
      uid,
      onError: (error) => {
        if (isPermissionDeniedError(error)) {
          setState({
            error: null,
            loading: false,
            mutationId: null,
            reaction: null,
          });
          return;
        }

        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        }));
      },
      onNext: (reaction, mutationId) => {
        setState({ error: null, loading: false, mutationId, reaction });
      },
    });
  }, [enabled, postId, uid]);

  return state;
}

export function usePostLike(postId: string, uid: string | undefined) {
  const state = usePostReaction(postId, uid);

  return {
    error: state.error,
    liked: state.reaction === 'like',
    loading: state.loading,
  };
}

export function useSetPostReaction(uid: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      mutationId,
      post,
      reaction,
    }: {
      mutationId?: string;
      post: PublicPost;
      reaction: Reaction | null;
    }): Promise<'queued' | 'saved'> => {
      if (!uid) {
        throw new Error('You must be signed in to react to posts.');
      }

      if (!(await isNetworkAvailable())) {
        await queueSetPostReaction({ mutationId, post, reaction, uid });
        return 'queued';
      }

      try {
        const writePromise = setPostReaction({
          mutationId,
          post,
          reaction,
          uid,
        });
        void writePromise.catch(() => undefined);
        await withReactionWriteTimeout(writePromise);
      } catch (error) {
        if (isRecoverableNetworkError(error)) {
          await queueSetPostReaction({ mutationId, post, reaction, uid });
          return 'queued';
        }

        throw error;
      }

      return 'saved';
    },
    onSuccess: async (_data, { post, reaction }) => {
      trackEvent('post_reaction_set', {
        post_author_is_current_user: post.authorUid === uid,
        reaction: reaction ?? 'clear',
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: profileStatsQueryKey(post.authorUid),
        }),
        queryClient.invalidateQueries({
          queryKey: publicProfileStatsQueryKey(post.authorUid),
        }),
      ]);
    },
  });
}

export function useTogglePostLike(uid: string | undefined) {
  const setReaction = useSetPostReaction(uid);

  return {
    ...setReaction,
    mutate: (
      input: { liked?: boolean; post: PublicPost },
      options?: Parameters<typeof setReaction.mutate>[1],
    ) =>
      setReaction.mutate(
        {
          post: input.post,
          reaction: input.liked ? 'like' : null,
        },
        options,
      ),
    mutateAsync: (input: { liked?: boolean; post: PublicPost }) =>
      setReaction.mutateAsync({
        post: input.post,
        reaction: input.liked ? 'like' : null,
      }),
  };
}
